"use client";

import type { GameData } from "@/app/games/(details)/types";
import type {
  LeaderboardData,
  LeaderboardEntry,
} from "@/components/QuestsV2/Points/Leaderboard/LeaderboardProvider";
import type { Campaign } from "@/components/QuestsV2/types";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RichTextRenderer } from "@/components/ui/custom/rich-text";
import { Separator } from "@/components/ui/separator";
import { IMMUTABLE_GAME_ID, IMMUTABLE_GAME_NAME } from "@/constants";
import { useGetLinkedAccounts } from "@/hooks";
import { cn } from "@/lib/utils";
import { openAuthorizeUrl } from "@/utils/linkedaccounts/linkedAccounts";
import { User } from "lucide-react";
import Image from "next/image";
import { forwardRef } from "react";
import { LeaderboardEntryItem } from "./LeaderboardEntryItem";
import { LeaderboardV2Skeleton } from "./LeaderboardV2Skeleton";
import {
  calculateEndTime,
  formatDate,
  formatNumber,
  formatWalletAddress,
} from "./utils";

export interface LeaderboardV2Props {
  campaign: Campaign;
  gameData: GameData;
  leaderboardData: LeaderboardData | null;
  isLoading?: boolean;
  isCreatorLeaderboard?: boolean;
}

// Constants
const AVATAR_COUNT = 4;
const STAR_IMAGE_SIZE = 64;
const TOKEN_ICON_SIZE = 56;
const DEFAULT_DESCRIPTION =
  "Compete in {campaignName} for rewards. Leaderboard points are based on participating in Game Quest Quests";
const DEFAULT_FOOTER_MESSAGE =
  "Come back every day for more opportunities to progress";

const CREATOR_FOOTER_MESSAGE = (startDate: Date, endDate: Date) =>
  `Post as many times as you like, from ${formatDate(startDate)} until ${formatDate(endDate)}.
Leaderboard updates every 24 hours at midnight UTC with a 2 day delay to allow for growth.
Low quality/relevance posts will be ignored.`;

const formatEntryName = (
  entry: LeaderboardEntry,
  isCreatorLeaderboard?: boolean,
  isCurrentUser = false,
  currentUserXAccount?: {
    platform: string;
    username: string;
    external_id: string;
  } | null,
) => {
  if (!isCreatorLeaderboard) {
    return formatWalletAddress(entry.walletAddress);
  }

  if (isCurrentUser && currentUserXAccount?.external_id) {
    return (
      <a
        href={`https://x.com/i/user/${currentUserXAccount.external_id}`}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:underline"
      >
        @{currentUserXAccount.username}
      </a>
    );
  }

  if (entry.xUsername && entry.xUserId) {
    return (
      <a
        href={`https://x.com/i/user/${entry.xUserId}`}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:underline"
      >
        @{entry.xUsername}
      </a>
    );
  }

  return formatWalletAddress(entry.walletAddress);
};

const CompetitionHeader = ({
  title,
  description,
  headline,
  tokenIcon,
  isCreatorLeaderboard,
  xUsername,
  hasLinkedXAccount,
  onPostToX,
  campaign,
}: {
  title: string;
  description: string;
  headline?: string;
  tokenIcon: string | undefined;
  isCreatorLeaderboard?: boolean;
  xUsername?: string;
  hasLinkedXAccount?: boolean;
  onPostToX?: () => void;
  campaign: Campaign;
}) => (
  <div className="space-y-8">
    <div className="flex items-center justify-between gap-3">
      <div className="space-y-2">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-black text-foreground truncate">
          {title}
        </h1>
        {headline && (
          <h4 className="text-heading-sm sm:text-heading-sm text-foreground/80 leading-5 sm:leading-6">
            {headline}
          </h4>
        )}
      </div>
      {tokenIcon && (
        <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
          <Image
            src={tokenIcon}
            alt={title}
            width={TOKEN_ICON_SIZE}
            height={TOKEN_ICON_SIZE}
          />
        </div>
      )}
    </div>
    <RichTextRenderer content={description} />
    {isCreatorLeaderboard &&
      xUsername &&
      onPostToX &&
      campaign.endDate > new Date() && (
        <div className="flex justify-center pb-2">
          <Button
            onClick={onPostToX}
            className="w-full max-w-xs bg-gradient-button text-white font-bold text-sm sm:text-md lg:text-lg py-4 px-4 rounded-full transition-all duration-200 shadow-lg hover:shadow-xl hover:opacity-90"
          >
            {hasLinkedXAccount
              ? `Post about ${xUsername} on X`
              : "Link X Account to participate"}
          </Button>
        </div>
      )}
  </div>
);

const ParticipantCount = ({
  totalParticipants,
}: { totalParticipants: number }) => (
  <div className="flex flex-col min-w-0">
    <span className="text-xs sm:text-sm text-muted-foreground">
      Participants
    </span>
    <span className="text-sm sm:text-base font-semibold text-foreground truncate">
      {formatNumber(totalParticipants)}
    </span>
  </div>
);

const ParticipantAvatars = ({
  totalParticipants,
}: { totalParticipants: number }) => (
  <div className="flex -space-x-4">
    {Array.from(
      { length: Math.min(totalParticipants, AVATAR_COUNT) },
      (_, i) => {
        const avatarNumber = i + 1;
        return (
          <Avatar
            key={`avatar-${avatarNumber}`}
            className="w-6 h-6 sm:w-8 sm:h-8"
          >
            <AvatarFallback className="bg-muted text-xs">
              <User className="w-3 h-3 sm:w-4 sm:h-4" />
            </AvatarFallback>
          </Avatar>
        );
      },
    )}
  </div>
);

const EndTimeDisplay = ({ endTime }: { endTime: string }) => (
  <div className="flex flex-col items-end">
    <span className="text-xs sm:text-sm text-muted-foreground">Ends</span>
    <span className="text-sm sm:text-base font-semibold text-foreground">
      {endTime}
    </span>
  </div>
);

const RewardInfo = ({
  totalParticipants,
  endTime,
}: {
  totalParticipants: number;
  endTime: string;
}) => (
  <div className="flex items-center justify-between w-full gap-4">
    <div className="flex items-center gap-2 sm:gap-3">
      <ParticipantCount totalParticipants={totalParticipants} />
      <ParticipantAvatars totalParticipants={totalParticipants} />
    </div>
    <EndTimeDisplay endTime={endTime} />
  </div>
);

const TopRankings = ({
  entries,
  isCreatorLeaderboard,
}: {
  entries: LeaderboardData["topEntries"];
  isCreatorLeaderboard?: boolean;
}) => (
  <div className="space-y-1">
    {entries.map((entry) => (
      <LeaderboardEntryItem
        key={entry.rank}
        entry={entry}
        displayName={formatEntryName(entry, isCreatorLeaderboard)}
      />
    ))}
  </div>
);

const UserRankHeader = () => (
  <div className="flex items-center gap-4 my-4">
    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider whitespace-nowrap">
      Your Rank
    </span>
    <Separator className="flex-1 bg-foreground/15" />
  </div>
);

const FooterMessage = ({
  message,
  showStar,
}: { message: string; showStar: boolean }) => (
  <div className="flex flex-col sm:flex-row items-center gap-5 p-4 bg-muted/20 rounded-lg shadow-sm">
    <div className="text-md text-muted-foreground flex-1 font-medium">
      <RichTextRenderer content={message} textAlign="left" />
    </div>

    <div className={`w-16 h-16 flex ${showStar ? "block" : "hidden"}`}>
      <Image
        src="/assets/referral/star.gif"
        alt="Star"
        width={STAR_IMAGE_SIZE}
        height={STAR_IMAGE_SIZE}
      />
    </div>
  </div>
);

const NoDataCard = ({ ref }: { ref: React.Ref<HTMLDivElement> }) => (
  <Card ref={ref} className={cn("w-full bg-background/20 backdrop-blur-xl")}>
    <CardContent className="p-6">
      <div className="text-center text-muted-foreground">
        <p>No leaderboard data available</p>
      </div>
    </CardContent>
  </Card>
);

export const LeaderboardV2 = forwardRef<HTMLDivElement, LeaderboardV2Props>(
  (
    {
      campaign,
      gameData,
      leaderboardData,
      isLoading = false,
      isCreatorLeaderboard = false,
    },
    ref,
  ) => {
    const { data: linkedAccounts } = useGetLinkedAccounts();
    const xAccount = linkedAccounts?.linked_accounts.find(
      (account) => account.platform === "x",
    );

    const hasLinkedXAccount = linkedAccounts?.linked_accounts.some(
      (account) => account.platform === "x",
    );

    const xUsername = campaign.contentMetadata?.xHandle;

    const handlePostToX = () => {
      if (hasLinkedXAccount && xUsername) {
        const xUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(xUsername)}`;
        window.open(xUrl, "_blank");
      } else {
        openAuthorizeUrl(
          "link-x-account",
          "x-v2",
          "/quests",
          IMMUTABLE_GAME_NAME,
          IMMUTABLE_GAME_ID,
          true,
        );
      }
    };

    const title = campaign.name;
    const headline = campaign.contentMetadata?.headline;
    const description =
      campaign.contentMetadata?.tagline ||
      DEFAULT_DESCRIPTION.replace("{campaignName}", campaign.name);
    const tokenIcon = gameData.details.logo_url;
    const endTime = calculateEndTime(new Date(campaign.endDate));
    const isCampaignEnded = campaign.endDate < new Date();
    const footerMessage = isCampaignEnded
      ? campaign.contentMetadata?.campaign_ended_message ||
        "This campaign has ended. Thank you for participating!"
      : campaign.contentMetadata?.rules ||
        (isCreatorLeaderboard
          ? CREATOR_FOOTER_MESSAGE(
              new Date(campaign.startDate),
              new Date(campaign.endDate),
            )
          : DEFAULT_FOOTER_MESSAGE);

    return (
      <Card
        ref={ref}
        className={cn("w-full bg-background/20 backdrop-blur-xl")}
      >
        <CardContent className="px-1 space-y-4 sm:space-y-5">
          <div className="space-y-5">
            <CompetitionHeader
              title={title}
              description={description}
              headline={headline}
              tokenIcon={tokenIcon}
              isCreatorLeaderboard={isCreatorLeaderboard}
              xUsername={xUsername}
              hasLinkedXAccount={hasLinkedXAccount}
              onPostToX={handlePostToX}
              campaign={campaign}
            />
            {leaderboardData ? (
              <>
                <RewardInfo
                  totalParticipants={leaderboardData.totalParticipants}
                  endTime={endTime}
                />

                <Separator className="bg-foreground/15" />

                {!isCampaignEnded && (
                  <TopRankings
                    entries={leaderboardData.topEntries}
                    isCreatorLeaderboard={isCreatorLeaderboard}
                  />
                )}

                {!isCampaignEnded && leaderboardData.userEntry && (
                  <>
                    <UserRankHeader />
                    <LeaderboardEntryItem
                      entry={leaderboardData.userEntry}
                      displayName={formatEntryName(
                        leaderboardData.userEntry,
                        isCreatorLeaderboard,
                        true,
                        xAccount,
                      )}
                      isCurrentUser={true}
                    />
                  </>
                )}
              </>
            ) : isLoading ? (
              <LeaderboardV2Skeleton ref={ref} />
            ) : (
              <NoDataCard ref={ref} />
            )}

            {!isCampaignEnded && <Separator className="bg-foreground/15" />}

            <FooterMessage message={footerMessage} showStar={isCampaignEnded} />
          </div>
        </CardContent>
      </Card>
    );
  },
);

LeaderboardV2.displayName = "LeaderboardV2";
